import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { ErrorsOf, IAbstractRecord } from '@models';
import { MAIN_THEME } from '@styles';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { trimTrailingZeros } from '@utils/formatting-utils';
import {
  enumListAsRecordObject,
  getError,
  lookupListAsRecordObject
} from '@utils/graph-utils';

import { isEmpty } from 'lodash';
import PrecisionService from 'src/core/services/PrecisionService';
import { makeStyles } from 'tss-react/mui';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import {
  IBaseDrawerProps,
  IDefaultCommissionInfo,
  IDefaultCommissionLovs
} from '@common/types';
import {
  useGetDefaultCommissionLovs,
  useModifyBusinessPartnerLineCommission
} from '@common/slices';
import { defaultCommissionDrawerSections } from '@common/constants';
import { useSelector } from 'react-redux';
import { RootState } from '@core/redux/store';

const mapToDrawerData = (
  data: IAbstractRecord,
  currencyLookup: Record<string, string> = {}
): {
  lovs: IDefaultCommissionLovs;
  data: IDefaultCommissionInfo;
} => {
  const lovs: IDefaultCommissionLovs = {
    applicableOn: lookupListAsRecordObject(
      data?.Core?.commissionApplicableOnOptions || []
    ),
    commissionType: enumListAsRecordObject(
      data?.Core_CommissionTypes?.enumValues || []
    ),
    valueCurrency: currencyLookup
  };

  const bpl = data?.salesforce?.getBusinessPartnerLineDetails;
  console.log(bpl);
  const mappedData: IDefaultCommissionInfo = {
    assignedLineName: `${bpl?.line.code ?? ''} - ${bpl?.line.name ?? ''}`,
    commissionType: bpl?.commissionType || 'RATE',
    lineCommissionValue: bpl?.commissionAmount || null,
    valueCurrency:
      bpl?.commissionAmountCurrency ||
      Object.keys(lovs.valueCurrency).find(
        (key) => lovs.valueCurrency[key] === 'USD..'
      ),
    lineCommissionRate:
      bpl?.commissionRate != null
        ? PrecisionService.multiplyBy100(bpl?.commissionRate)
        : null,
    applicableOn: bpl?.commissionApplicableOn || ''
  };

  return {
    lovs,
    data: mappedData
  };
};

const useStyles = makeStyles()(() => ({
  note: {
    color: 'red !important',
    fontSize: `${MAIN_THEME.fontSize.xs} !important`,
    fontStyle: 'italic'
  }
}));

interface Props extends IBaseDrawerProps {
  businessPartnerId: string;
  assignedLineId: string;
}
const SetLineDefaultCommissionDrawer: React.FC<Props> = ({
  businessPartnerId,
  assignedLineId,
  onSuccess
}) => {
  const { classes } = useStyles();
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const { data: lovsData } = useGetDefaultCommissionLovs.useQuery({
    options: {
      variables: { id: assignedLineId }
    }
  });

  const [values, setValues] = useState<IDefaultCommissionInfo>({
    assignedLineName: '',
    commissionType: '',
    lineCommissionValue: null,
    valueCurrency: '',
    lineCommissionRate: null,
    applicableOn: ''
  });
  const [lovs, setLovs] = useState<IDefaultCommissionLovs>({
    applicableOn: {},
    commissionType: {}
  });
  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>(
    defaultCommissionDrawerSections(values, lovs)
  );

  const [modifyLineCommission] = useModifyBusinessPartnerLineCommission();

  const currencyLookup: Record<string, string> = {};
  lovsData?.core?.getCurrencies?.forEach((currency: any) => {
    currencyLookup[currency.id] = currency.title;
  });

  const initialize = async () => {
    try {
      if (!lovsData) return;

      const currencyLookup: Record<string, string> = {};
      lovsData?.core?.getCurrencies?.forEach((currency: any) => {
        currencyLookup[currency.id] = currency.title;
      });

      const mappedEnum = enumListAsRecordObject(
        lovsData.Core_CommissionTypes?.enumValues || []
      );

      const applicableOnLookup: Record<string, string> = {};
      lovsData.planConfig.getCommissionApplicableOnOptions.forEach(
        (item: any) => {
          applicableOnLookup[item.id] = item.title;
        }
      );

      const { data, lovs } = mapToDrawerData(lovsData, currencyLookup);

      lovs.commissionType = mappedEnum;
      lovs.applicableOn = applicableOnLookup;

      if (!data || !lovs) return;
      console.log(data);
      const newSections = defaultCommissionDrawerSections(data, lovs);
      setLovs(lovs);
      setValues(data);
      setInputsForm(newSections);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  useEffect(() => {
    if (lovsData) initialize();
  }, [lovsData]);

  const submitForm = async (formValues: IAbstractRecord) => {
    const [data] = normalizeDynamicFormDrawerValues(
      defaultCommissionDrawerSections(
        formValues as unknown as IDefaultCommissionInfo,
        lovs
      ),
      formValues
    );

    try {
      const isRate = data.commissionType === 'RATE';

      const input = {
        businessPartnerId,
        businessPartnerLineId: assignedLineId,
        commissionType: data.commissionType,
        commissionRate: isRate
          ? PrecisionService.divideBy100(data.lineCommissionRate)
          : null,
        commissionApplicableOn: isRate ? data.applicableOn : null,
        commissionAmountCurrency: isRate ? null : data.valueCurrency || '39735',
        commissionAmount: isRate
          ? null
          : trimTrailingZeros(data.lineCommissionValue)
      };

      const res = await modifyLineCommission({
        input
      });

      if (isEmpty(res.errors)) {
        onSuccess();
      }
    } catch (err) {
      toast.error(<ToastErrorMessage>{getError(err)}</ToastErrorMessage>);
    }
  };

  const onChange = async (
    fieldName: keyof IDefaultCommissionInfo,
    value: IDefaultCommissionInfo[keyof IDefaultCommissionInfo],
    sectionIndex: number,
    newValues: IDefaultCommissionInfo,
    errors: ErrorsOf<IDefaultCommissionInfo>
  ) => {
    (values[fieldName] as typeof value) = value;

    setValues(newValues);
  };

  return (
    <DynamicFormWidget
      sections={inputsForm}
      onSubmit={(values) => submitForm(values)}
      submitButtonState={submitButtonState}
      onChange={onChange}
      noteSection={{
        className: classes.note,
        text: 'Note that changing the default commissions will only affect assigned plans in the future. This change will not affect previously assigned plans.'
      }}
    />
  );
};

export { SetLineDefaultCommissionDrawer };
